import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const LINE_COLORS = [
    '#0784a7', '#f59e0b', '#10b981', '#e74c3c', '#8e44ad',
    '#3498db', '#e67e22', '#1abc9c', '#c0392b', '#9b59b6',
    '#2ecc71', '#d35400', '#16a085', '#8b0000', '#6c5ce7',
];

const TeamLineChart = ({ teamMembers, assessmentsStore, companyId, weeks = 12 }) => {
    const data = useMemo(() => {
        const now = new Date();
        const day = now.getUTCDay();
        const currentWeekStart = new Date(now);
        currentWeekStart.setUTCDate(now.getUTCDate() - day + (day === 0 ? -6 : 1));
        currentWeekStart.setUTCHours(0, 0, 0, 0);

        const weekStarts = [];
        for (let i = weeks - 1; i >= 0; i--) {
            const ws = new Date(currentWeekStart);
            ws.setUTCDate(ws.getUTCDate() - i * 7);
            weekStarts.push(ws);
        }

        // Gather all assessments per member
        const memberAssessments = {};
        teamMembers.forEach(member => {
            try {
                memberAssessments[member.id] = (assessmentsStore.closedAssessmentsForUser(member.id, companyId) || [])
                    .filter(a => a.completedDateTime);
            } catch (e) {
                memberAssessments[member.id] = [];
            }
        });

        return weekStarts.map(ws => {
            const weekEnd = new Date(ws);
            weekEnd.setUTCDate(weekEnd.getUTCDate() + 7);
            const month = ws.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
            const dayNum = ws.getUTCDate();

            const entry = { week: `${month} ${dayNum}` };

            teamMembers.forEach(member => {
                entry[member.name] = (memberAssessments[member.id] || []).filter(a => {
                    const d = new Date(a.completedDateTime);
                    return d >= ws && d < weekEnd;
                }).length;
            });

            return entry;
        });
    }, [teamMembers, assessmentsStore, weeks]);

    const hasData = data.some(w => teamMembers.some(m => w[m.name] > 0));

    if (!hasData) {
        return (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: '14px', fontFamily: 'Montserrat' }}>
                    No team practice data yet.
                </div>
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fontFamily: 'Montserrat', fill: '#888' }} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fontFamily: 'Montserrat', fill: '#888' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: '11px', borderRadius: '6px', border: '1px solid #e0e0e0' }} labelStyle={{ fontWeight: 600, marginBottom: '4px' }} />
                <Legend wrapperStyle={{ fontFamily: 'Montserrat', fontSize: '10px' }} iconType="circle" iconSize={8} />
                {teamMembers.map((member, i) => (
                    <Line
                        key={member.id}
                        type="monotone"
                        dataKey={member.name}
                        stroke={LINE_COLORS[i % LINE_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

export default TeamLineChart;
